'use client'
import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
  type TouchEvent as ReactTouchEvent,
} from 'react'
import { SlArrowLeft, SlArrowRight } from 'react-icons/sl'

export default function CustomSwiper({
  children,
  displayPriceDailyRate,
  autoplay = true,
  autoplaySpeed = 4000,
  speed = 300,
  showDots = true,
  showArrows = false,
  className = '',
  touchEnabled = true,
  activeDotColor = 'bg-white',
  inactiveDotColor = 'bg-white',
  slidesToShow = 1,
  dotClassName = 'bottom-[8px]',
  showPrevious = false,
  onSlideChange = undefined,
  maxDots = 6,
  infinite = true,
  ishome = false,
  dotSize = 6,
  containerClassName = '',
  responsiveSlides,
}: {
  displayPriceDailyRate?: string | number
  children: ReactNode
  autoplay?: boolean
  autoplaySpeed?: number
  speed?: number
  showDots?: boolean
  showArrows?: boolean
  className?: string
  touchEnabled?: boolean
  activeDotColor?: string
  inactiveDotColor?: string
  slidesToShow?: number
  dotClassName?: string
  showPrevious?: boolean
  onSlideChange?: (index: number) => void
  maxDots?: number
  infinite?: boolean
  ishome?: boolean
  dotSize?: number
  containerClassName?: string
  responsiveSlides?: { maxWidth: number; slidesToShow: number }[]
}) {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [touchStart, setTouchStart] = useState(0)
  const [touchEnd, setTouchEnd] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const [isMouseDown, setIsMouseDown] = useState(false)
  const [dragOffset, setDragOffset] = useState(0)
  const [effectiveSlidesToShow, setEffectiveSlidesToShow] = useState(slidesToShow)

  // Use refs for dragging state inside autoplay interval to avoid recreating interval on drag
  const isDraggingRef = useRef(false)
  const isMouseDownRef = useRef(false)

  const containerRef = useRef<HTMLDivElement>(null)
  const autoplayRef = useRef<NodeJS.Timeout | null>(null)
  const slides = Array.isArray(children) ? children : [children]
  const totalSlides = slides.length
  // Last valid starting index — beyond this point the viewport would show empty space
  const maxIndex = Math.max(totalSlides - effectiveSlidesToShow, 0)

  // Sync dragging state to refs
  const setIsDraggingSync = (v: boolean) => {
    isDraggingRef.current = v
    setIsDragging(v)
  }
  const setIsMouseDownSync = (v: boolean) => {
    isMouseDownRef.current = v
    setIsMouseDown(v)
  }

  // Debounced responsive breakpoint check — runs only when width crosses a breakpoint
  useEffect(() => {
    const updateBreakpoint = () => {
      const width = window.innerWidth
      if (responsiveSlides?.length) {
        const sorted = [...responsiveSlides].sort((a, b) => a.maxWidth - b.maxWidth)
        const match = sorted.find((r) => width <= r.maxWidth)
        const newCount = match ? match.slidesToShow : slidesToShow
        setEffectiveSlidesToShow(newCount)
        setCurrentSlide((prev) => Math.min(prev, Math.max(totalSlides - newCount, 0)))
      } else {
        setEffectiveSlidesToShow(slidesToShow)
        setCurrentSlide((prev) => Math.min(prev, Math.max(totalSlides - slidesToShow, 0)))
      }
    }
    updateBreakpoint()

    let timer: ReturnType<typeof setTimeout>
    const handleResize = () => {
      clearTimeout(timer)
      timer = setTimeout(updateBreakpoint, 150)
    }
    window.addEventListener('resize', handleResize, { passive: true })
    return () => {
      window.removeEventListener('resize', handleResize)
      clearTimeout(timer)
    }
  }, [responsiveSlides, slidesToShow, totalSlides])

  // Autoplay — stable callback using refs, not re-created on drag state changes
  const startAutoplay = useCallback(() => {
    if (totalSlides <= 1) return
    if (autoplayRef.current) clearInterval(autoplayRef.current)
    if (autoplay) {
      const maxIdx = Math.max(totalSlides - effectiveSlidesToShow, 0)
      autoplayRef.current = setInterval(() => {
        if (!isDraggingRef.current && !isMouseDownRef.current) {
          setCurrentSlide((prev) => {
            if (infinite) return prev >= maxIdx ? 0 : prev + 1
            return prev < maxIdx ? prev + 1 : prev
          })
        }
      }, autoplaySpeed)
    }
  }, [autoplay, autoplaySpeed, totalSlides, infinite, effectiveSlidesToShow])

  // Start autoplay only when carousel enters viewport (IntersectionObserver)
  useEffect(() => {
    if (!autoplay || totalSlides <= 1) return
    const el = containerRef.current
    if (!el) return

    let started = false
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !started) {
          started = true
          startAutoplay()
          observer.disconnect()
        }
      },
      { threshold: 0.1 }
    )
    observer.observe(el)

    return () => {
      observer.disconnect()
      if (autoplayRef.current) clearInterval(autoplayRef.current)
    }
  }, [autoplay, totalSlides, startAutoplay])

  // Callback for slide changes
  useEffect(() => {
    onSlideChange?.(currentSlide)
  }, [currentSlide, onSlideChange])

  // Touch handlers
  const handleTouchStart = (e: ReactTouchEvent<HTMLDivElement>) => {
    if (!touchEnabled || totalSlides <= 1) return
    setTouchEnd(0)
    setTouchStart(e.targetTouches[0].clientX)
    setIsDraggingSync(true)
    setDragOffset(0)
    if (autoplayRef.current) clearInterval(autoplayRef.current)
  }

  const handleTouchMove = (e: ReactTouchEvent<HTMLDivElement>) => {
    if (!touchEnabled || totalSlides <= 1) return
    const currentTouch = e.targetTouches[0].clientX
    setTouchEnd(currentTouch)

    if (touchStart && containerRef.current) {
      const diff = currentTouch - touchStart
      const containerWidth = containerRef.current.offsetWidth
      const maxOffset = containerWidth * 0.3
      setDragOffset(Math.max(-maxOffset, Math.min(maxOffset, diff)))
    }
  }

  const handleTouchEnd = () => {
    if (!touchEnabled || totalSlides <= 1) return

    setIsDraggingSync(false)
    setDragOffset(0)

    if (!touchStart || !touchEnd) {
      startAutoplay()
      return
    }

    const distance = touchStart - touchEnd
    if (distance > 15) {
      setCurrentSlide((prev) => (infinite ? (prev >= maxIndex ? 0 : prev + 1) : Math.min(prev + 1, maxIndex)))
    } else if (distance < -15) {
      setCurrentSlide((prev) => (infinite ? (prev === 0 ? maxIndex : prev - 1) : Math.max(prev - 1, 0)))
    }

    setTouchStart(0)
    setTouchEnd(0)
    startAutoplay()
  }

  // Mouse handlers
  const handleMouseDown = (e: ReactMouseEvent<HTMLDivElement>) => {
    const target = e.target

    if (
      !touchEnabled ||
      totalSlides <= 1 ||
      !(target instanceof Element) ||
      target.closest('[data-no-drag]')
    ) {
      return
    }

    e.preventDefault()
    setTouchEnd(0)
    setTouchStart(e.clientX)
    setIsMouseDownSync(true)
    setIsDraggingSync(true)
    setDragOffset(0)
    if (autoplayRef.current) clearInterval(autoplayRef.current)
  }

  const handleMouseMove = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (!touchEnabled || !isMouseDown || totalSlides <= 1) return
    e.preventDefault()

    const currentMouse = e.clientX
    setTouchEnd(currentMouse)

    if (touchStart && containerRef.current) {
      const diff = currentMouse - touchStart
      const containerWidth = containerRef.current.offsetWidth
      const maxOffset = containerWidth * 0.3
      setDragOffset(Math.max(-maxOffset, Math.min(maxOffset, diff)))
    }
  }

  const handleMouseUp = (e: ReactMouseEvent<HTMLDivElement>) => {
    if (!touchEnabled || !isMouseDown || totalSlides <= 1) return
    e.preventDefault()

    setIsMouseDownSync(false)
    setIsDraggingSync(false)
    setDragOffset(0)

    if (!touchStart || !touchEnd) {
      startAutoplay()
      return
    }

    const distance = touchStart - touchEnd
    if (distance > 15) {
      setCurrentSlide((prev) => (infinite ? (prev >= maxIndex ? 0 : prev + 1) : Math.min(prev + 1, maxIndex)))
    } else if (distance < -15) {
      setCurrentSlide((prev) => (infinite ? (prev === 0 ? maxIndex : prev - 1) : Math.max(prev - 1, 0)))
    }

    setTouchStart(0)
    setTouchEnd(0)
    startAutoplay()
  }

  const handleMouseLeave = () => {
    if (!touchEnabled || !isMouseDown || totalSlides <= 1) return
    setIsMouseDownSync(false)
    setIsDraggingSync(false)
    setDragOffset(0)
    startAutoplay()
  }

  // Navigation functions
  const goToSlide = (index: number) => {
    setCurrentSlide(index)
    startAutoplay()
  }

  const goToNextSlide = (e?: ReactMouseEvent<HTMLButtonElement>) => {
    e?.preventDefault()
    e?.stopPropagation()
    setCurrentSlide((prev) => (infinite ? (prev >= maxIndex ? 0 : prev + 1) : Math.min(prev + 1, maxIndex)))
    startAutoplay()
  }

  const goToPrevSlide = (e?: ReactMouseEvent<HTMLButtonElement>) => {
    e?.preventDefault()
    e?.stopPropagation()
    setCurrentSlide((prev) => (infinite ? (prev === 0 ? maxIndex : prev - 1) : Math.max(prev - 1, 0)))
    startAutoplay()
  }

  // Calculate which dots to show
  const getVisibleDots = () => {
    const pageCount = maxIndex + 1
    if (pageCount <= maxDots) return Array.from({ length: pageCount }, (_, i) => i)

    let start = 0
    if (currentSlide < Math.floor(maxDots / 2)) {
      start = 0
    } else if (currentSlide > pageCount - Math.ceil(maxDots / 2)) {
      start = pageCount - maxDots
    } else {
      start = currentSlide - Math.floor(maxDots / 2)
    }
    return Array.from({ length: maxDots }, (_, i) => start + i)
  }

  const visibleDots = getVisibleDots()

  return (
    <div className={`relative w-full h-full ${className}`}>
      {/* Slider container */}
      <div
        ref={containerRef}
        className={`relative w-full sm:w-full h-full overflow-hidden ${containerClassName} ${touchEnabled && totalSlides > 1
            ? 'cursor-pointer'
            : ''
          }`}
        onTouchStart={touchEnabled && totalSlides > 1 ? handleTouchStart : undefined}
        onTouchMove={touchEnabled && totalSlides > 1 ? handleTouchMove : undefined}
        onTouchEnd={touchEnabled && totalSlides > 1 ? handleTouchEnd : undefined}
        onMouseDown={touchEnabled && totalSlides > 1 ? handleMouseDown : undefined}
        onMouseMove={touchEnabled && totalSlides > 1 ? handleMouseMove : undefined}
        onMouseUp={touchEnabled && totalSlides > 1 ? handleMouseUp : undefined}
        onMouseLeave={touchEnabled && totalSlides > 1 ? handleMouseLeave : undefined}
        style={{
          touchAction: touchEnabled && totalSlides > 1 ? 'pan-y pinch-zoom' : 'auto',
        }}
      >
        {/* Slides wrapper */}
        <div
          className='flex w-full h-full'
          style={{
            transform: `translateX(calc(-${currentSlide * (100 / effectiveSlidesToShow)}% + ${dragOffset}px + ${showPrevious && currentSlide > 0 ? '10%' : '0%'
              }))`,
            transition: isDragging ? 'none' : `transform ${speed}ms ease-out`,
          }}
        >
          {slides.map((slide, index) => (
            <div
              key={index}
              data-active={currentSlide === index}
              className='flex-shrink-0 h-full relative'
              style={{ width: `${100 / effectiveSlidesToShow}%` }}
            >
              {/* Price tag - only show on current slide when ishome is true and price is provided */}
              {ishome && displayPriceDailyRate && index === currentSlide && (
                <div className='absolute top-[21.5px] md:right-4 right-4 w-fit h-[27px] opacity-100 rounded-[6px] px-[10px] md:px-[10px] py-[4px] gap-[10px] bg-[#F0F0F0CC] text-center items-center justify-center z-10 flex'>
                  <span className='font-medium text-[12px] leading-[160%] tracking-[0] text-[#191919]'>
                    From{' '}
                    {(displayPriceDailyRate || 0)}
                  </span>
                </div>
              )}
              <div className='h-full w-full'>{slide}</div>
            </div>
          ))}
        </div>
      </div>

      {/* Navigation Arrows */}
      {showArrows && totalSlides > 1 && (
        <>
          {(infinite || currentSlide > 0) && (
            <button
              onClick={goToPrevSlide}
              onMouseDown={(e) => e.stopPropagation()}
              className='absolute top-1/2 left-2.5 transform -translate-y-1/2 bg-white p-3 rounded-full shadow cursor-pointer z-10 hover:bg-gray-50 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center'
              aria-label='Previous slide'
            >
              <SlArrowLeft size={11} />
            </button>
          )}
          {(infinite || currentSlide < maxIndex) && (
            <button
              onClick={goToNextSlide}
              onMouseDown={(e) => e.stopPropagation()}
              className='absolute top-1/2 right-2.5 transform -translate-y-1/2 bg-white p-3 rounded-full shadow cursor-pointer z-10 hover:bg-gray-50 transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center'
              aria-label='Next slide'
            >
              <SlArrowRight size={11} />
            </button>
          )}
        </>
      )}

      {/* Dots indicator */}
      {showDots && totalSlides > 1 && (
        <div
          className={`flex space-x-1.5 md:space-x-2 absolute left-1/2 bottom-3 z-10 m-0 p-0 transform -translate-x-1/2 ${dotClassName}`}
          role='tablist'
          aria-label='Slide navigation'
        >
          {visibleDots.map((slideIndex) => (
            <button
              key={slideIndex}
              type='button'
              role='tab'
              aria-selected={currentSlide === slideIndex}
              aria-label={`Go to slide ${slideIndex + 1}`}
              onClick={() => goToSlide(slideIndex)}
              className={`block rounded-full transition-all duration-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${currentSlide === slideIndex
                  ? `${activeDotColor} opacity-100`
                  : `${inactiveDotColor} opacity-60`
                }`}
              style={{ width: `${dotSize}px`, height: `${dotSize}px` }}
            />
          ))}
        </div>
      )}
    </div>
  )
}
